import './QuillToolbar.scss';

import * as React from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';

import QuillToolBarLinkButton from '~shared/components/quill/QuillToolbarLinkButton';
import { TooltipV2 } from '~shared/components/TooltipV2';

import { QuillToolbarButton } from './QuillToolbarButton';
import {
  Bold,
  Italic,
  Subscript,
  Superscript,
  Underline,
} from './QuillToolbarIcons';
import QuillToolBarMathButton from './QuillToolbarMathButton';

interface Props {
  isVisible: boolean;
  onMathButtonPress?: () => void;
  isMathFormulaSelected: boolean;
  isLinkSelected: boolean;
  onLinkButtonPress?: () => void;
}

const QuillToolbar: React.RefForwardingComponent<
  HTMLDivElement,
  React.PropsWithChildren<Props>
> = (
  {
    isVisible,
    onMathButtonPress,
    isMathFormulaSelected,
    isLinkSelected,
    onLinkButtonPress,
  }: React.PropsWithChildren<Props>,
  ref: React.Ref<HTMLDivElement>
) => {
  const classNames = cx(
    'relative flex flex-wrap transition-all duration-100 ease-out mix-multiply',
    'quill-toolbar',
    {
      'visible max-h-20 py-2 opacity-100': isVisible,
      'invisible max-h-0 opacity-0': !isVisible,
    }
  );

  return (
    <div ref={ref} className={classNames}>
      <div className="absolute right-0 bottom-0 left-0 h-px mx-4 bg-divider" />
      <span className="ql-formats">
        <TooltipV2 enablePersistence label={translate('Bold')}>
          <div>
            <QuillToolbarButton
              label={translate('Bold')}
              disabled={isMathFormulaSelected}
              className="ql-bold"
            >
              <Bold />
            </QuillToolbarButton>
          </div>
        </TooltipV2>
        <TooltipV2 enablePersistence label={translate('Italic')}>
          <div>
            <QuillToolbarButton
              label={translate('Italic')}
              disabled={isMathFormulaSelected}
              className="ql-italic"
            >
              <Italic />
            </QuillToolbarButton>
          </div>
        </TooltipV2>
        <TooltipV2 enablePersistence label={translate('Underline')}>
          <div>
            <QuillToolbarButton
              label={translate('Underline')}
              disabled={isMathFormulaSelected}
              className="ql-underline"
            >
              <Underline />
            </QuillToolbarButton>
          </div>
        </TooltipV2>
      </span>
      <span className="ql-formats">
        <TooltipV2 enablePersistence label={translate('Subscript')}>
          <div>
            <QuillToolbarButton
              label={translate('Subscript')}
              disabled={isMathFormulaSelected}
              className="ql-script"
              value="sub"
            >
              <Subscript />
            </QuillToolbarButton>
          </div>
        </TooltipV2>
        <TooltipV2 enablePersistence label={translate('Superscript')}>
          <div>
            <QuillToolbarButton
              label={translate('Superscript')}
              disabled={isMathFormulaSelected}
              className="ql-script"
              value="super"
            >
              <Superscript />
            </QuillToolbarButton>
          </div>
        </TooltipV2>
        <TooltipV2 enablePersistence label={translate('Link')}>
          <div>
            <QuillToolBarLinkButton
              disabled={isMathFormulaSelected}
              onClick={onLinkButtonPress}
              isLinkSelected={isLinkSelected}
            />
          </div>
        </TooltipV2>
      </span>
      <span className="ql-formats">
        <TooltipV2 enablePersistence label={translate('Equation')}>
          <div>
            <QuillToolBarMathButton
              onClick={onMathButtonPress}
              isMathFormulaSelected={isMathFormulaSelected}
            />
          </div>
        </TooltipV2>
      </span>
    </div>
  );
};

export default React.forwardRef(QuillToolbar);
